/**
 * ABM de roles: el entregable central de la etapa 1. Un rol pertenece a una
 * aplicacion, o es global cuando gobierna la propia consola del SSO.
 */

import { useMemo, useState, type FormEvent } from 'react';
import { Button } from '@presentation/components/common';
import { RequireRole } from '@presentation/router/RequireRole';
import { useApplications, useRoles } from '@presentation/hooks';
import { SYSTEM_ROLES } from '@shared/constants';
import type { Role } from '@domain/entities';
import styles from '../admin.module.css';

const GLOBAL_SCOPE = 'GLOBAL';

export function RolesPage() {
  const { applications } = useApplications();
  const [scope, setScope] = useState<string>('');
  const [editing, setEditing] = useState<Role | null>(null);
  const [form, setForm] = useState({ code: '', name: '', description: '' });

  const filters = useMemo(
    () => ({
      applicationId: scope && scope !== GLOBAL_SCOPE ? scope : undefined,
      onlyGlobal: scope === GLOBAL_SCOPE,
    }),
    [scope]
  );

  const { roles, isLoading, error, save, toggleStatus } = useRoles(filters);

  const applicationName = (applicationId: string | null) =>
    applicationId
      ? (applications.find(application => application.id === applicationId)?.name ?? applicationId)
      : 'Global (SSO)';

  const startEdit = (role: Role) => {
    setEditing(role);
    setForm({ code: role.code, name: role.name, description: role.description ?? '' });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm({ code: '', name: '', description: '' });
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    const saved = await save(
      {
        applicationId: scope && scope !== GLOBAL_SCOPE ? scope : undefined,
        code: form.code,
        name: form.name,
        description: form.description || undefined,
      },
      editing?.id
    );

    if (saved) cancelEdit();
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Roles</h1>
          <p className={styles.subtitle}>
            El SSO entrega estos codigos en el token; cada aplicacion los traduce a sus permisos.
          </p>
        </div>
      </header>

      <div className={styles.toolbar}>
        <select
          className={styles.select}
          value={scope}
          onChange={event => {
            setScope(event.target.value);
            cancelEdit();
          }}
          aria-label="Ambito"
        >
          <option value="">Todos los ambitos</option>
          <option value={GLOBAL_SCOPE}>Global (SSO)</option>
          {applications.map(application => (
            <option key={application.id} value={application.id}>
              {application.name}
            </option>
          ))}
        </select>
      </div>

      {error && <p className={styles.error}>{error}</p>}

      <RequireRole roles={[SYSTEM_ROLES.admin]}>
        <form className={styles.form} onSubmit={submit}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="roleCode">
              Codigo
            </label>
            <input
              id="roleCode"
              className={styles.input}
              value={form.code}
              onChange={event => setForm({ ...form, code: event.target.value })}
              placeholder="OPERACIONES"
              disabled={!!editing}
              required
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="roleName">
              Nombre
            </label>
            <input
              id="roleName"
              className={styles.input}
              value={form.name}
              onChange={event => setForm({ ...form, name: event.target.value })}
              required
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="roleDescription">
              Descripcion
            </label>
            <input
              id="roleDescription"
              className={styles.input}
              value={form.description}
              onChange={event => setForm({ ...form, description: event.target.value })}
            />
          </div>

          <div className={styles.actions}>
            <Button type="submit" disabled={!editing && !scope}>
              {editing ? 'Guardar cambios' : 'Crear rol'}
            </Button>
            {editing && (
              <Button type="button" variant="secondary" onClick={cancelEdit}>
                Cancelar
              </Button>
            )}
          </div>

          {!editing && !scope && (
            <p className={styles.subtitle}>Elegi un ambito para crear el rol.</p>
          )}
        </form>
      </RequireRole>

      {isLoading ? (
        <p className={styles.empty}>Cargando roles...</p>
      ) : roles.length === 0 ? (
        <p className={styles.empty}>No hay roles en este ambito.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Codigo</th>
              <th>Nombre</th>
              <th>Ambito</th>
              <th>Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {roles.map(role => (
              <tr key={role.id}>
                <td>
                  <code>{role.code}</code>
                  {role.isSystem && (
                    <span className={`${styles.badge} ${styles.badgeNeutral}`}> sistema</span>
                  )}
                </td>
                <td>{role.name}</td>
                <td>{applicationName(role.applicationId)}</td>
                <td>
                  <span
                    className={`${styles.badge} ${
                      role.status === 'ACTIVE' ? styles.badgeActive : styles.badgeInactive
                    }`}
                  >
                    {role.status === 'ACTIVE' ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td>
                  <RequireRole roles={[SYSTEM_ROLES.admin]}>
                    <div className={styles.actions}>
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => startEdit(role)}
                        disabled={role.isSystem}
                      >
                        Editar
                      </Button>
                      <Button
                        size="small"
                        variant={role.status === 'ACTIVE' ? 'danger' : 'primary'}
                        onClick={() => void toggleStatus(role)}
                        disabled={role.isSystem}
                      >
                        {role.status === 'ACTIVE' ? 'Dar de baja' : 'Reactivar'}
                      </Button>
                    </div>
                  </RequireRole>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
